import type { PendingChangeRequestResponseRow } from '@risksmart-app/drizzle/src/types';
import type { GetLivePendingChangeRequestsSubscription } from '@risksmart-app/web-graphql-client/generated/graphql';
import { GetLivePendingChangeRequestsDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { createSubscriptionHook } from 'src/utils';

import { mapChangeRequestRowsToFragment } from './mapChangeRequestRowsToFragment';

/**
 * The Pending→terminal flip is settled asynchronously by the merge consumer /
 * other approvers, so the responder's own invalidation races it and the V3
 * stack has no subscription transport to push it — the feed is POLLED. 5s
 * matches `useGetChangeRequestsByParentSubscription`'s panel poll.
 */
const POLL_INTERVAL_MS = 5000;

/**
 * Holding-pattern subscription hook for a parent's PENDING change requests
 * (RSP-6099). The live overlay on governed-entity forms reads this to know a CR
 * is in flight and to render its requested changes.
 *
 * - `trpc` ON  → polls `frontend.changeRequest.pendingChangeRequests` and maps
 *   the V3 blob into the legacy (V2) `ChangeRequestParts` shape the forms were
 *   built against (the cross-generation mapper runs server-side on this feed).
 * - `trpc` OFF → the legacy Hasura `getLivePendingChangeRequests` subscription.
 *
 * Built on `createSubscriptionHook` so the poll → real tRPC subscription swap
 * (RSP-4320) is a one-place change. Replaces the raw `useSubscription` that
 * previously lived inline in `useChangeRequests` and bypassed the V3→V2 mapper.
 */
export const useGetLivePendingChangeRequestsSubscription =
  createSubscriptionHook<
    { parentId: string },
    PendingChangeRequestResponseRow[],
    GetLivePendingChangeRequestsSubscription
  >({
    trpcQueryOptions: (trpc, { parentId }) =>
      trpc.frontend.changeRequest.pendingChangeRequests.queryOptions({
        parentId,
      }),
    trpcRefetchInterval: () => POLL_INTERVAL_MS,
    mapTrpcDataToGraphQL: (rows) => ({
      change_request: mapChangeRequestRowsToFragment(rows),
    }),
    graphqlDocument: GetLivePendingChangeRequestsDocument,
    graphqlVariables: ({ parentId }) => ({ ParentId: parentId }),
  });
